import { useMemo } from "react";
import type { UniversalEntityPageProps } from "../../types";
import type { BaseEntity } from "../../../UniversalCRMTable/types";
import { useUniversalEntityPageAuth } from "../useUniversalEntityPageAuth";

/**
 * Extracts and computes auth state for composition
 * Handles auth setup and canCreate computation
 */
export function useCompositionAuth<T extends BaseEntity>(
  entityName: string,
  props: UniversalEntityPageProps<T>
) {
  const auth = useUniversalEntityPageAuth();
  const {
    appUser,
    isAuthenticated,
    isClientUser,
    authLoading,
    isEmployee,
    isAdmin,
    isWTCCRM,
    isWTCEmployee,
    isAdminRole,
    isMobile,
    canCreate: baseCanCreate,
  } = auth;

  // Auth gate is handled by UniversalEntityPage: we only run composition when
  // we have an app user. No early returns here so hook count stays stable.
  if (!appUser) {
    throw new Error(
      "useCompositionAuth must only be used when appUser exists (gate in UniversalEntityPage)"
    );
  }

  // Compute canCreate with entity name
  const canCreate = useMemo(() => {
    return (
      baseCanCreate &&
      (isWTCCRM ||
        isAdminRole ||
        (isWTCEmployee && ["tickets", "time_entries", "tasks"].includes(entityName)))
    );
  }, [baseCanCreate, isWTCCRM, isAdminRole, isWTCEmployee, entityName]);

  return {
    appUser,
    isAuthenticated,
    isClientUser,
    authLoading,
    isEmployee,
    isAdmin,
    isWTCCRM,
    isWTCEmployee,
    isAdminRole,
    isMobile,
    canCreate,
  };
}
